import {
  addMinutes,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  parse,
  setHours,
  setMinutes,
  setSeconds,
} from "date-fns";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  CardContent,
  CircularProgress,
  DatePicker,
  DesktopTimePicker,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  LocalizationProvider,
  MaskedInput,
  MenuItem,
  Select,
  TextField,
} from "../../../components";
import { InputErros, value } from "../../../helpers";
import {
  AdzapService,
  AgendamentoServicosService,
  MensagemPersonalizadaService,
  notification,
  PessoaService,
  ServicoService,
  SessionService,
} from "../../../services";
import { formatDate, responseErros } from "../../../utils";
import AgendamentoServicosValidator from "./middlewares/agendamento-servicos.validator";

const AddAgendamentoDialog = ({
  open,
  setOpen,
  dataSelecionada,
  setDataSelecionada,
  agendamento,
  setAgendamento,
  agendamentosList,
  setAgendamentosMesList,
  horariosList,
  setHorariosList,
  horariosFuncionamento,
  filtrarHorariosDisponiveis,
  buscarAgendamentos,
  buscarAgendamentosMes,
  formatFilterDate,
  dadosEmpresa,
  setLoading,
}) => {
  const inputServico = useRef();
  const inputCliente = useRef();
  const inputPrestadorServico = useRef();
  const [clienteList, setClienteList] = useState([]);
  const [prestadorServicoList, setPrestadorServicoList] = useState([]);
  const [servicoList, setServicoList] = useState([]);
  const [horariosPrestador, setHorariosPrestador] = useState([]);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);
  const [loadingAutoCompleteServico, setLoadingAutoCompleteServico] =
    useState(false);
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  const pessoaService = new PessoaService();
  const servicoService = new ServicoService();
  const agendamentoServicosService = new AgendamentoServicosService();
  const mensagemPersonalizadaService = new MensagemPersonalizadaService();
  const adzapService = new AdzapService();
  const sessionService = new SessionService();
  const empresaUsuario = sessionService.getEmpresaAndUsuario();
  const configuracoes = sessionService.getConfiguracoes();

  const tiposPessoa = [
    {
      tipoId: 1,
      setLista: (dados) => setClienteList(dados),
    },
    {
      tipoId: 5,
      setLista: (dados) => setPrestadorServicoList(dados),
    },
  ];

  function onCloseDialog() {
    setAgendamento({});
    setOpen(false);
  }

  async function buscarServicoById(id) {
    const result = await servicoService.getById(id);
    if (!result.isAxiosError) {
      setServicoList([result.data]);
    } else {
      responseErros(result);
    }
  }

  useEffect(() => {
    if (open) {
      if (agendamento?.prestadorServicoId) {
        buscarPessoasPorTipo({ id: agendamento?.prestadorServicoId }, 5);
      }
      if (agendamento?.servicoId) {
        buscarServicoById(agendamento.servicoId);
      }

      if (configuracoes.intervaloAgendamentos && horariosFuncionamento.length) {
        let horarios = filtrarHorariosDisponiveis(
          agendamento?.prestadorServicoId,
        );
        if (agendamento?.id) {
          const horarioAgendamento = format(
            new Date(agendamento.hora),
            "HH:mm",
          );
          horarios = [...horarios, horarioAgendamento].sort();
        }
        setHorariosList(horarios);
        setHorariosPrestador(horarios);
      }
    }
  }, [open]);

  const onSearchChangePessoas = async (event, inputRef, tiposPessoaId) => {
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      const eventValue = event.target?.value;
      let filtros = {
        nomeRazaoSocial: eventValue,
        apelidoFantasia: eventValue,
      };
      setLoadingAutoComplete(true);
      buscarPessoasPorTipo(filtros, tiposPessoaId);
    }
  };
  const buscarPessoasPorTipo = async (filtros, tiposPessoaId) => {
    const filtro = {
      ...filtros,
      tiposPessoaId,
      nonPaginated: true,
      ativado: true,
    };
    const tipoPessoaTemp = tiposPessoa.find(
      ({ tipoId }) => tipoId === tiposPessoaId,
    );
    const pessoas = await buscarPessoas(filtro);
    setLoadingAutoComplete(false);
    if (pessoas?.length) {
      tipoPessoaTemp?.setLista(pessoas);
    }
  };

  async function buscarPessoas(filtros) {
    const res = await pessoaService.getAll(filtros);
    if (!res.isAxiosError) {
      setLoadingAutoComplete(false);
      return res.data;
    } else {
      responseErros(res);
    }
    setLoadingAutoComplete(false);
    return [];
  }

  const onChangeAutocompleteCliente = (newValue) => {
    setAgendamento((prevState) => ({
      ...prevState,
      clienteId: newValue?.id,
      clienteNome: newValue?.nomeRazaoSocial,
      celular: newValue?.celular,
    }));
  };

  const onInputChangeServicos = (event) => {
    if (
      event.key === "Enter" &&
      inputServico.current === document.activeElement
    ) {
      const eventValue = event.target?.value;
      if (eventValue && eventValue.trim().length > 0) {
        setLoadingAutoCompleteServico(true);
        buscarServicos(eventValue);
      }
    }
  };

  const buscarServicos = async (termoBusca) => {
    const filtros = {
      nome: termoBusca,
      descricao: termoBusca,
      codigo: termoBusca,
      ativado: true,
    };

    try {
      const response = await servicoService.getAll(filtros);
      setLoadingAutoCompleteServico(false);

      if (!response.isAxiosError && response.data) {
        setServicoList(response.data.rows);
      } else {
        responseErros(response);
        setServicoList([]);
      }
    } catch (error) {
      setLoadingAutoCompleteServico(false);
      setServicoList([]);
      console.error("Erro ao buscar serviços:", error);
    }
  };

  const adicionarAgendamentoMes = (agendamentos) => {
    const dataReferencia = format(
      formatDate.received(agendamento.periodo),
      "yyyy-MM-dd",
    );
    const periodo = agendamentos.find(({ dia }) => dataReferencia === dia);
    if (periodo) {
      return agendamentos.map((item) =>
        item.data === dataReferencia
          ? { ...item, total: item.total + 1 }
          : item,
      );
    } else {
      return [
        ...agendamentos,
        {
          dia: dataReferencia,
          total: 1,
        },
      ];
    }
  };

  function validarHorarioFuncionamento(time) {
    const intervalosFuncionamento =
      horariosFuncionamento.find(
        (item) => dataSelecionada.getDay() === item.diaSemana,
      )?.intervalos ?? [];

    for (const intervalo of intervalosFuncionamento) {
      const abertura = parse(intervalo.abertura, "HH:mm:ss", new Date()),
        fechamento = parse(intervalo.fechamento, "HH:mm:ss", new Date());

      if (agendamento?.servicoId) {
        const servico = value.autoComplete(servicoList, agendamento.servicoId);
        const terminoServico = addMinutes(time, servico.tempoExecucao);
        if (
          !(isBefore(time, abertura) || isAfter(terminoServico, fechamento))
        ) {
          return true;
        }
      } else if (!(isBefore(time, abertura) || isAfter(time, fechamento))) {
        return true;
      }
    }

    return false;
  }

  function validarHorarioManual(periodo) {
    const horaString = format(periodo, "HH:mm");
    const time = parse(horaString, "HH:mm", new Date());

    const validacao = validarHorarioFuncionamento(time);
    if (!validacao) return false;

    const agendamentosPrestador = agendamentosList
      .filter(
        ({ id, prestadorServicoId }) =>
          id !== agendamento?.id &&
          prestadorServicoId === agendamento?.prestadorServicoId,
      )
      .map(({ periodo, tempoServico }) => ({
        horario: format(new Date(periodo), "HH:mm"),
        tempoServico,
      }));

    for (const horarioPrestador of agendamentosPrestador) {
      const start = parse(horarioPrestador.horario, "HH:mm", new Date()),
        end = addMinutes(start, horarioPrestador.tempoServico ?? 0);

      if (isWithinInterval(time, { start, end })) {
        return false;
      }

      if (agendamento?.servicoId) {
        const servico = value.autoComplete(servicoList, agendamento.servicoId);
        const terminoServico = addMinutes(time, servico.tempoExecucao);
        if (isBefore(start, terminoServico) && isBefore(time, end)) {
          return false;
        }
      }
    }

    return true;
  }

  function combinarDataHora(data, hora) {
    let resultado = setHours(data, hora.getHours());
    resultado = setMinutes(resultado, hora.getMinutes());
    resultado = setSeconds(resultado, hora.getSeconds());
    return resultado;
  }

  const handleSubmit = async () => {
    setLoading(true);
    if (!agendamento.data || !agendamento.hora) {
      notification.alertaGenericos("Selecione um horário válido");
      setLoading(false);
      return;
    }

    const diasFuncionamento = configuracoes.diasSemanaFuncionamento;
    if (diasFuncionamento && agendamento?.data) {
      if (!diasFuncionamento.includes(new Date(agendamento?.data).getDay())) {
        notification.alertaGenericos("Este dia da semana não está disponível");
        setLoading(false);
        return;
      }
    }

    const periodo = combinarDataHora(agendamento.data, agendamento.hora);
    if (!configuracoes.intervaloAgendamentos && horariosFuncionamento.length) {
      const validacao = validarHorarioManual(periodo);
      if (!validacao) {
        setLoading(false);
        notification.alertaGenericos("Horário indisponível");
        return;
      }
    }

    const agendamentoTemp = {
      ...agendamento,
      configuracaoTempoServico: configuracoes.intervaloAgendamentos,
      periodo,
    };
    AgendamentoServicosValidator.validate(agendamentoTemp, {
      abortEarly: false,
    })
      .then(async () => {
        let mensagemLembrete = null,
          anexoLembrete = null;
        if (
          (dadosEmpresa.whatsappSimples || dadosEmpresa.whatsappAvancado) &&
          dadosEmpresa.configuracoes?.lembreteAntecedenciaServico
        ) {
          const { mensagem, anexo } = await enviarMensagemPorContexto(
            agendamentoTemp,
            "lembreteAgendamentoServico",
          );
          mensagemLembrete = mensagem;
          anexoLembrete = anexo;
        }
        const body = {
          ...agendamento,
          lembreteAntecedencia:
            dadosEmpresa.configuracoes?.lembreteAntecedenciaServico,
          mensagemLembrete,
          anexoLembrete,
          periodo,
        };
        if (agendamento?.id) {
          const response = await agendamentoServicosService.atualizar(
            agendamento?.id,
            body,
          );
          setLoading(false);
          if (!response.isAxiosError) {
            onCloseDialog();
            notification.alteracaoSucesso();
            if (!isSameDay(dataSelecionada, agendamentoTemp.periodo)) {
              setDataSelecionada(agendamentoTemp.periodo);
            }
            buscarAgendamentosMes({ mesReferencia: agendamentoTemp.periodo });
            const porIntervalo = formatFilterDate(agendamentoTemp.periodo);
            buscarAgendamentos({ porIntervalo });
          } else {
            responseErros(response);
          }
        } else {
          const retornoAlerta = await notification.confirmacao(
            "Confirmação",
            `Deseja confirmar o agendamento para ${format(
              formatDate.received(agendamentoTemp.periodo),
              "dd/MM/yyyy 'às' HH:mm?",
            )}`,
          );
          if (retornoAlerta.isConfirmed) {
            const response = await agendamentoServicosService.cadastrar(body);
            if (!response.isAxiosError) {
              notification.cadastroSucesso();
              await enviarConfirmacaoAgendamento(body);
              setAgendamentosMesList((prev) => adicionarAgendamentoMes(prev));
              buscarAgendamentosMes({ mesReferencia: agendamentoTemp.periodo });
              const porIntervalo = formatFilterDate(agendamentoTemp.periodo);
              buscarAgendamentos({ porIntervalo });
              if (!response.data?.mensagemEnviada) {
                notification.alertaGenericos(
                  "Lembrete não pode ser enviado. Favor verificar a conexão!",
                );
              }
              onCloseDialog();
            } else {
              responseErros(response);
            }
          }
        }
      })
      .catch((err) => {
        if (err.isAxiosError) {
          responseErros(err);
        } else {
          inputErros.set(err);
        }
      })
      .finally(() => setLoading(false));
  };

  async function enviarConfirmacaoAgendamento(agendamento) {
    const { mensagem, anexo } = await enviarMensagemPorContexto(
      agendamento,
      "agendamentoServico",
    );

    const requestBody = {
      number: agendamento.celular,
      type: "text",
      text: mensagem,
    };
    if (anexo) {
      requestBody.type = "image";
      requestBody.file = anexo;
    }

    await adzapService.enviarMensagem(requestBody);
  }

  async function enviarMensagemPorContexto(agendamento, contexto) {
    const getPrimeiroNome = (nomeCompleto) => nomeCompleto?.split(" ")[0] || "";

    const texto = await mensagemPersonalizadaService.getMensagem({
      contexto,
      apelidoFantasia: empresaUsuario.empresa?.fantasia,
      nomeCompletoCliente: agendamento.clienteNome,
      primeiroNomeCliente: getPrimeiroNome(agendamento.clienteNome),
      nomeUsuario: empresaUsuario?.usuario?.nome,
      dataAgendamento: format(agendamento.periodo, "dd/MM/yyyy"),
      horaAgendamento: format(agendamento.periodo, "HH:mm"),
    });

    if (texto.isAxiosError) {
      throw texto;
    }

    const mensagemContexto = await mensagemPersonalizadaService.getAll({
      contexto,
    });
    const anexo = mensagemContexto.data.rows[0].anexo;

    return { mensagem: texto.data, anexo };
  }

  function validarHorariosDisponiveis(
    tempoServico,
    tempoAgendamento,
    horarios,
  ) {
    const resultados = [],
      proporcao = tempoServico / tempoAgendamento;
    for (let i = 0; i < horarios.length; i++) {
      const atual = horarios[i];
      let success = true,
        atualTemp = parse(atual, "HH:mm", new Date());

      for (let j = 1, k = i + 1; j < proporcao; j++, k++) {
        atualTemp = addMinutes(atualTemp, tempoAgendamento);
        const dataFormatada = format(atualTemp, "HH:mm");
        const proximo = horarios[k];
        if (dataFormatada !== proximo) {
          success = false;
          break;
        }
      }

      if (success) resultados.push(atual);
    }

    return resultados;
  }

  const handleChangeServico = (value) => {
    if (configuracoes.intervaloAgendamentos && value && value.tempoExecucao) {
      const horarios = validarHorariosDisponiveis(
        value.tempoExecucao,
        configuracoes.intervaloAgendamentos,
        horariosPrestador,
      );
      if (!horarios.length) {
        notification.alertaGenericos(
          "Não há horários disponíveis para este serviço",
        );
        return;
      }
      setHorariosList(horarios);
    }
    setAgendamento((prev) => ({
      ...prev,
      servicoId: value?.id,
      tempoServico: value?.tempoExecucao,
    }));
  };

  const handleChangePrestadorServico = (value) => {
    if (configuracoes.intervaloAgendamentos && horariosFuncionamento.length) {
      const agendaPrestador = filtrarHorariosDisponiveis(value?.id);
      let horariosServicos = agendaPrestador;
      if (agendamento.servicoId && agendamento.tempoServico) {
        horariosServicos = validarHorariosDisponiveis(
          agendamento.tempoServico,
          configuracoes.intervaloAgendamentos,
          agendaPrestador,
        );
        if (!horariosServicos.length) {
          notification.alertaGenericos(
            "Não há horários disponíveis para este serviço",
          );
          return;
        }
      }
      setHorariosList(horariosServicos);
      setHorariosPrestador(agendaPrestador);
    }

    setAgendamento((prev) => ({
      ...prev,
      prestadorServicoId: value?.id,
    }));
  };

  const handleChangeTextfield = (event) => {
    setAgendamento((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const buscarAgendamentosDia = async (
    filtros,
    dataSelecionada,
    tempoExecucao,
  ) => {
    setLoading(true);
    const result = await agendamentoServicosService.getAllAgendamentosDia(
      filtros,
    );

    if (!result.isAxiosError) {
      const horarios = filtrarHorariosDisponiveis(
        null,
        result.data,
        dataSelecionada,
      );
      const resultados = validarHorariosDisponiveis(
        tempoExecucao,
        configuracoes.intervaloAgendamentos,
        horarios,
      );
      if (!resultados.length) {
        notification.alertaGenericos("Não há horários disponíveis neste dia");
        setAgendamento((prev) => ({
          ...prev,
          hora: null,
        }));
        setHorariosList([]);
        setLoading(false);
        return;
      }

      if (agendamento.hora) {
        const horarioSelecionado = format(agendamento.hora, "HH:mm");
        if (!resultados.find((item) => item === horarioSelecionado)) {
          setAgendamento((prev) => ({
            ...prev,
            hora: null,
          }));
        }
      }
      setLoading(false);
      setHorariosList(resultados);
    } else {
      responseErros(result);
      setLoading(false);
      return;
    }
  };

  const atualizarHorariosDisponiveis = (dataReferencia, prestadorServicoId) => {
    if (configuracoes.intervaloAgendamentos) {
      const servico = servicoList.find(
        ({ id }) => id === agendamento?.servicoId,
      );
      const filtroTemp = {
        diaReferencia: formatDate.toSend(dataReferencia),
        prestadorServicoId,
      };
      buscarAgendamentosDia(
        filtroTemp,
        dataReferencia,
        servico?.tempoExecucao ?? configuracoes.intervaloAgendamentos,
      );
    }
  };

  const onChangeDate = (name, date) => {
    if (!date) {
      date = "Invalid Date";
    }

    if (date !== "Invalid Date") {
      const diasFuncionamento = configuracoes.diasSemanaFuncionamento;
      if (diasFuncionamento?.includes(date.getDay())) {
        atualizarHorariosDisponiveis(date, agendamento.prestadorServicoId);
      } else if (diasFuncionamento) {
        notification.alertaGenericos("Este dia da semana não está disponível");
      }
    }

    setAgendamento((prev) => ({
      ...prev,
      [name]: date != "Invalid Date" ? date : "",
    }));
  };

  const handleChangeHora = (time) => {
    if (!time) return;

    setAgendamento((prev) => ({
      ...prev,
      hora: time != "Invalid Date" ? time : null,
    }));
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      PaperComponent="div"
      PaperProps={{
        style: {
          borderRadius: "0.7rem",
          background: "white",
        },
      }}
      open={open}
      onClose={() => onCloseDialog()}
    >
      <DialogContent>
        <h6 className="text-uppercase fw-bold pt-2 mx-3">ADICIONAR SERVIÇO</h6>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                id="clienteId"
                name="clienteId"
                options={clienteList}
                autoHighlight
                getOptionLabel={(option) => {
                  let codigoPessoa = option?.codigo
                    ? `${option.codigo} - `
                    : "";
                  let descricaoPessoa =
                    codigoPessoa + `${option.nomeRazaoSocial}`;
                  return option ? descricaoPessoa : "";
                }}
                loading={loadingAutoComplete}
                loadingText="Carregando"
                noOptionsText={
                  document.getElementById("clienteId")?.value.length >= 1 ? (
                    <Grid container spacing={1}>
                      <Grid item xs={12} fullWidth>
                        Nenhum cliente encontrado
                      </Grid>
                    </Grid>
                  ) : (
                    "Digite e pressione Enter"
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={"Buscar Cliente"}
                    variant={"outlined"}
                    onKeyDown={(e) => onSearchChangePessoas(e, inputCliente, 1)}
                    inputRef={inputCliente}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Fragment>
                            {loadingAutoComplete ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(_, newValue) => {
                  onChangeAutocompleteCliente(newValue);
                }}
                value={value.autoComplete(clienteList, agendamento?.clienteId)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="clienteNome"
                name="clienteNome"
                label="Cliente"
                variant="outlined"
                disabled={agendamento?.clienteId}
                onChange={handleChangeTextfield}
                value={agendamento?.clienteNome ?? ""}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                id="servicoId"
                name="servicoId"
                options={servicoList}
                autoHighlight
                getOptionLabel={(option) =>
                  option ? `${option?.codigo} - ${option?.nome}` : ""
                }
                loading={loadingAutoCompleteServico}
                loadingText="Carregando"
                noOptionsText={
                  document.getElementById("servicoId")?.value.length >= 1 ? (
                    <Grid container spacing={1}>
                      <Grid item xs={12} fullWidth>
                        Nenhum serviço encontrado
                      </Grid>
                    </Grid>
                  ) : (
                    "Digite e pressione Enter"
                  )
                }
                onChange={(_, value) => handleChangeServico(value)}
                value={value.autoComplete(servicoList, agendamento?.servicoId)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={"Buscar Serviço"}
                    variant={"outlined"}
                    onKeyDown={onInputChangeServicos}
                    inputRef={inputServico}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Fragment>
                            {loadingAutoComplete ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                id="prestadorServicoId"
                name="prestadorServicoId"
                options={prestadorServicoList}
                autoHighlight
                getOptionLabel={(option) => {
                  let codigoPessoa = option?.codigo
                    ? `${option.codigo} - `
                    : "";
                  let descricaoPessoa =
                    codigoPessoa + `${option.nomeRazaoSocial}`;
                  return option ? descricaoPessoa : "";
                }}
                loading={loadingAutoComplete}
                loadingText="Carregando"
                noOptionsText={
                  document.getElementById("prestadorServicoId")?.value.length >=
                  1 ? (
                    <Grid container spacing={1}>
                      <Grid item xs={12} fullWidth>
                        Nenhum prestador encontrado
                      </Grid>
                    </Grid>
                  ) : (
                    "Digite e pressione Enter"
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={"Buscar Prestador de Serviço"}
                    variant={"outlined"}
                    onKeyDown={(e) =>
                      onSearchChangePessoas(e, inputPrestadorServico, 5)
                    }
                    inputRef={inputPrestadorServico}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Fragment>
                            {loadingAutoComplete ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(_, newValue) =>
                  handleChangePrestadorServico(newValue)
                }
                value={value.autoComplete(
                  prestadorServicoList,
                  agendamento?.prestadorServicoId,
                )}
              />
            </Grid>
            <Grid item xs={5}>
              <MaskedInput
                id="celular"
                label="Celular"
                name="celular"
                margin="none"
                mask="(99) 9 9999-9999"
                InputLabelProps={{ shrink: true }}
                onChange={handleChangeTextfield}
                value={agendamento?.celular ?? ""}
                InputProps={{
                  endAdornment: (
                    <IconButton disabled>
                      <i
                        className="ph-fill ph-device-mobile"
                        style={{
                          color: "#494C62",
                          fontSize: 20,
                        }}
                      ></i>
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <DatePicker
                id="data"
                name="data"
                label="Data"
                format="dd/MM/yyyy"
                disabled={!agendamento?.id}
                minDate={new Date()}
                value={value.date(agendamento?.data)}
                onChange={(date) => onChangeDate("data", date)}
              />
            </Grid>
            <Grid item xs={3}>
              {configuracoes.intervaloAgendamentos ? (
                <Select
                  id="hora"
                  name="hora"
                  value={
                    agendamento?.hora
                      ? format(new Date(agendamento?.hora), "HH:mm")
                      : ""
                  }
                  label="Hora"
                  fullWidth
                  disableClearable
                  onChange={(e) => {
                    const horaFormatada = parse(
                      e.target.value,
                      "HH:mm",
                      new Date(),
                    );
                    handleChangeHora(horaFormatada);
                  }}
                >
                  {horariosList.map((horario, index) => (
                    <MenuItem key={`${horario}-${index}`} value={horario}>
                      {horario}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <LocalizationProvider>
                  <DesktopTimePicker
                    sx={{ width: "100%" }}
                    name="hora"
                    label="Hora"
                    onChange={handleChangeHora}
                    value={agendamento?.hora ?? null}
                  />
                </LocalizationProvider>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="observacao"
                name="observacao"
                label="Observação"
                variant="outlined"
                value={agendamento?.observacao ?? ""}
                onChange={handleChangeTextfield}
                InputProps={{ inputProps: { maxLength: 50 } }}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                color="padrao"
                onClick={() => onCloseDialog()}
                className="mt-3 rounded-3 fw-bolder"
                disableElevation
              >
                FECHAR
              </Button>
            </Grid>
            <Grid item xs={9}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => handleSubmit()}
                className="mt-3 rounded-3 fw-bolder"
                disableElevation
              >
                CONFIRMAR AGENDAMENTO
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </DialogContent>
    </Dialog>
  );
};

export default AddAgendamentoDialog;
